import axios, { Method } from 'axios';
import qs from 'qs';
import posthog from 'posthog-js';
import { RootState } from 'Douleutaras/redux/store';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import { getCurrentLanguage, useLocalization } from 'Douleutaras/hooks/localization';
import { useGetParams } from 'Douleutaras/hooks/get-params';
import { URLS } from 'Douleutaras/constants/urls';
import { getErrorMessage } from 'Douleutaras/redux/getErrorMessage';
import { CaptureMessage } from 'Douleutaras/providers/CaptureException';
import { clearUserAndRedirect } from 'Douleutaras/redux/data/auth/authSlice';

import { MakeRequestParams, MakeRequestParamsConfigure } from './apiTypes';

/**
 * This function is starting point for User to make an API Call Journey.
 * First, you need to call "makeRequest". This function will be called "apiRequest".
 * "apiRequest" -> will have "onApiSuccess" on success and "onApiError" on error.
 * After the "onApiError" function has completed, middlewares will be called.
 * "logger" middleware first will be called, which will process and display errors into the console.
 * After that, "crashReporter" middleware will be called, which will pass an error to Sentry
 * and end the chain of calls.
 *
 * 1. "makeRequest" -> 2. "apiRequest" -> 3. ("onApiSuccess" | "onApiError") -> 4. "logger"(middleware) -> 5. "crashReporter"(middleware)
 */
export const makeRequest = (url, { method, body, includes, additionalHeaders, additionalGetParams, stringifyOptions, configure }:
  MakeRequestParams = {}) => {
  return api => {
    if (!api) {
      throw Error('api passed to makeRequest() is undefined, check that you have correct number of args in createAsyncThunk (should be 2)');
    }

    return apiRequest(url,
      method,
      body,
      includes,
      additionalHeaders,
      additionalGetParams,
      stringifyOptions,
      configure,
      axiosBaseConfiguration)(api);
  };
};

const axiosBaseConfiguration = {
  baseUrl: `${COUNTRY_CONFIG.API_URL[(useLocalization()).countryCode]}`,
  maxRedirects: 0,
  prepareHeaders: (headers, getState: () => RootState | null, withCredentials: boolean) => {
    const params = useGetParams();
    if (getState !== undefined) {
      const token = (getState() as RootState).auth.token || params.token;
      if (withCredentials && token) {
        headers['Authorization'] = `JWT ${token}`;
      }
    }

    // Default to the language the current page actually rendered in (URL-aware on anonymized
    // routes) rather than letting the backend infer it from Accept-Language/cookie. Callers that
    // pass their own `languagecode` via additionalHeaders (e.g. saving a translation in a
    // language other than the one currently rendered) still take precedence.
    if (headers['languagecode'] === undefined) {
      headers['languagecode'] = getCurrentLanguage();
    }

    return headers;
  }
};

const onApiSuccess = response => {
  return response;
};

const onApiError = async (error, api) => {
  const status = error.response?.status;

  if (status === 401) {
    // Redirect to the login page only when we're not on the login page already
    if (window.location.href !== URLS.AUTHENTICATION.LOGIN) {
      await clearUserAndRedirect(api.dispatch, `${window.location.pathname}${window.location.search}`);

      return;
    }
  }
  // Handle any other status code here.
  // (e.g. error.response.status === 400)
  // (e.g. error.response.status === 500)

  const message = getErrorMessage(error);

  // @Sentry
  CaptureMessage(message);

  return api.rejectWithValue(error);
};

export const apiRequest = (url,
                           method: Method = 'GET',
                           data = {},
                           includes = [],
                           additionalHeaders = {},
                           additionalGetParams = {},
                           stringifyOptions = {},
                           configure: MakeRequestParamsConfigure = {
                             withCredentials: true
                           },
                           { prepareHeaders, baseUrl } = axiosBaseConfiguration) => {

  let extraHeaders = {};
  const v3 = url.indexOf('/v3/') !== -1;
  let parsedQueryParams = url.indexOf('?') === - 1 ? {} : qs.parse(url.split('?')[1]);

  if (includes.length) {
    parsedQueryParams.include = includes.join(',');
  }

  if (Object.keys(additionalGetParams).length > 0) {
    parsedQueryParams = { ...parsedQueryParams, ...additionalGetParams };
  }

  if (!v3) {
    parsedQueryParams['format'] = 'json';
  }

  const stringifiedQueryParams = qs.stringify(parsedQueryParams, stringifyOptions);
  const urlAppendix = stringifiedQueryParams ? `?${stringifiedQueryParams}` : '';

  if (Object.keys(additionalHeaders).length > 0) {
    extraHeaders = { ...extraHeaders, ...additionalHeaders };
  }

  // @TODO change this workaround. Legacy and new API accept different types of content-type
  extraHeaders['Content-Type'] = v3 ? 'application/vnd.api+json' : 'application/json';

  if (posthog) {
    extraHeaders['X-POSTHOG-DISTINCT-ID'] = posthog.get_distinct_id();
    extraHeaders['X-POSTHOG-SESSION-ID'] = posthog.get_session_id();
  }

  const readyUrl = `${baseUrl}${url.split('?')[0]}${urlAppendix}`;

  return api => {
    return axios({
      headers: prepareHeaders(extraHeaders, api ? api.getState : undefined, configure.withCredentials),
      // Add trailing slash to url if not exists
      url: readyUrl.replace(/\/?(\?|#|$)/, '/$1'),
      method,
      data,
      withCredentials: configure.withCredentials
    })
      .then(onApiSuccess)
      .catch(err => onApiError(err, api));
  };
};
